<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const { t } = useI18n()
const commerceStore = useCommerceStore()

const searchTerm = ref('')
const searchInput = ref<HTMLInputElement | null>(null)

// Debounced search function
const debouncedSearch = useDebounceFn((value: string) => {
  commerceStore.searchTerm = value
}, 300)

// Watch for search term changes and apply debounced search
watch(searchTerm, (newValue) => {
  debouncedSearch(newValue)
})

// Watch for store search term changes (e.g., when cleared via double ESC)
watch(() => commerceStore.searchTerm, (newValue) => {
  // Sync modal input with store when cleared externally
  if (!newValue && searchTerm.value) {
    searchTerm.value = ''
  }
})

// Methods
function closeModal() {
  emit('update:visible', false)
}

function applySearch() {
  // Immediate search on submit (no debounce)
  commerceStore.searchTerm = searchTerm.value
  closeModal()
  // Don't clear searchTerm here - keep it for when modal reopens
}

function clearSearch() {
  searchTerm.value = ''
  commerceStore.clearSearch()
}

// Auto-focus input when dialog is shown
function onDialogShow() {
  // The autofocus attribute on the input handles focus automatically
}
</script>

<template>
  <AppDialog
    :visible="visible"
    modal
    :header="t('Search')"
    :pt="{ content: { class: 'h-auto p-0' }, header: { class: 'bg-gray-800 text-white p-3' } }"
    position="top"
    @update:visible="$emit('update:visible', $event)"
    @show="onDialogShow"
  >
    <form
      class="p-4"
      @submit.prevent="applySearch"
    >
      <div class="flex flex-col gap-4">
        <!-- Search Input -->
        <input
          ref="searchInput"
          v-model="searchTerm"
          type="text"
          :placeholder="t('Search products...')"
          class="w-full p-inputtext"
          autofocus
        >

        <!-- Keyboard shortcuts hint -->
        <div class="text-xs text-gray-500">
          <span class="mr-3">
            <kbd class="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">ESC</kbd>
            {{ t('to close') }}
          </span>
          <span>
            <kbd class="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">ESC ESC</kbd>
            {{ t('to clear search') }}
          </span>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2 justify-end">
          <Button
            type="button"
            :label="t('Cancel')"
            severity="secondary"
            @click="closeModal"
          />
          <Button
            type="button"
            :label="t('Clear')"
            severity="secondary"
            icon="pi pi-times"
            @click="clearSearch"
          />
          <Button
            type="submit"
            :label="t('Search')"
            icon="pi pi-search"
          />
        </div>
      </div>
    </form>
  </AppDialog>
</template>
